import type { GameState } from './types';
import type { BoardGraph } from '@shared/board/graph';
import type { DistanceCache } from '@shared/board/distance-cache';
import { respawnPlayer } from './respawn';
import { clearCursedRelicForPlayer } from './cursed-relic-lockout';

/**
 * Orchestrate the post-death flow for a player who has just died (isDead === true, hp === 0).
 *
 * Responsibility:
 * - Clears transient Cursed Relic lockout before respawn presentation.
 * - Calls respawnPlayer to move the dead player to the furthest graveyard,
 *   restore HP to 30, clear isDead, and set besideGraveyardId (beside-immunity).
 * - Returns the graveyard node ID where the player was placed.
 *
 * Does NOT emit death or damage events — those are already emitted by applyDamage.
 * May emit item_dot_expired for Cursed Relic, then emits RespawnEvent via respawnPlayer.
 *
 * Caller contract:
 * - applyDamage must have been called first (player.isDead === true).
 * - State already contains the death and damage events in the event log.
 *
 * @param state         - Game state to mutate (player will be updated in place).
 * @param playerId      - ID of the dead player to process.
 * @param graph         - Board graph for graveyard node lookup.
 * @param distanceCache - Pre-computed distance cache for furthest graveyard selection.
 * @returns The graveyard node ID where the player was respawned.
 * @throws Error if playerId is not found, or no graveyard nodes exist on the board.
 */
export function handleDeath(
  state: GameState,
  playerId: string,
  graph: BoardGraph,
  distanceCache: DistanceCache,
): string {
  clearCursedRelicForPlayer(state, playerId);
  // respawnPlayer handles: isDead=false, hp=30, move to graveyard, besideImmunity, RespawnEvent
  respawnPlayer(state, playerId, graph, distanceCache);

  const player = state.players.get(playerId)!;
  return player.nodeId; // The graveyard node ID where the player was placed
}

export interface PendingDeathHandled {
  playerId: string;
  graveyardNodeId: string;
}

/**
 * Respawn players that are currently marked dead.
 *
 * When candidateIds are provided, only those players are considered and their
 * order is preserved. Without candidates, turn order is used first, then any
 * remaining players in insertion order, which keeps simultaneous handling
 * deterministic even if a test fixture omits a player from turnOrder.
 */
export function handlePendingDeaths(
  state: GameState,
  graph: BoardGraph,
  distanceCache: DistanceCache,
  candidateIds?: Iterable<string>,
): PendingDeathHandled[] {
  const orderedIds =
    candidateIds == null
      ? [
          ...state.turnOrder,
          ...Array.from(state.players.keys()).filter(
            (playerId) => !state.turnOrder.includes(playerId),
          ),
        ]
      : Array.from(candidateIds);
  const seen = new Set<string>();
  const handled: PendingDeathHandled[] = [];

  for (const playerId of orderedIds) {
    if (seen.has(playerId)) continue;
    seen.add(playerId);

    const player = state.players.get(playerId);
    if (player?.isDead !== true) continue;

    const graveyardNodeId = handleDeath(state, playerId, graph, distanceCache);
    handled.push({ playerId, graveyardNodeId });
  }

  return handled;
}
